// pkg198 — dashboard card linking to Payment methods & autopay — delivered 2026-11-01
// pkg181 — statusForModule: map asset_ledger → status.assetLedger (was falling to default → "Not started" forever) — delivered 2026-10-09
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg57 — "Documents from your firm" section: documents the firm shared to this portal, one-tap Word download — delivered 2026-07-09
// pkg21 — dashboard cards for assigned firm-private AI modules — delivered 2026-06-11
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../utils/api';
import { useFirmSettings } from '../contexts/FirmSettingsContext';
import { useClientModules } from '../contexts/ClientModulesContext';

// Map a module key to the corresponding fields in the status object that
// the existing GET /api/status endpoint returns. Content modules (PDF and
// HTML) have no submission state by design — they're read-only references.
function statusForModule(key, status) {
  switch (key) {
    case 'juvenile_info':   return { submitted: status?.juvenileInfo?.submitted_at,  saved: status?.juvenileInfo?.updated_at };
    case 'demographic':     return { submitted: status?.demographic?.submitted_at,   saved: status?.demographic?.updated_at };
    case 'budget':          return { submitted: status?.budget?.submitted_at,        saved: status?.budget?.updated_at };
    case 'parenting_plan':  return { submitted: null,                                 saved: status?.parentingPlan?.last_saved };
    case 'witnesses':       return { submitted: status?.witnesses?.submitted_at,     saved: status?.witnesses?.updated_at };
    case 'asset_ledger':    return { submitted: status?.assetLedger?.submitted_at,   saved: status?.assetLedger?.updated_at }; // pkg181
    default:                return { submitted: null,                                 saved: null };
  }
}

// Path resolution mirrors components/Layout.js — built-ins use their seeded
// React Router path, content modules go through /modules/:id.
function moduleHref(m) {
  if (m.module_type === 'builtin') return m.route || '/';
  return `/modules/${m.id}`;
}

export default function DashboardPage() {
  const { client } = useAuth();
  const { firm } = useFirmSettings();
  const { modules, firmModules } = useClientModules();
  const [links, setLinks]             = useState([]);
  const [dates, setDates]             = useState([]);
  const [globalDates, setGlobalDates] = useState([]);
  const [status, setStatus]           = useState(null);
  const [trust, setTrust]             = useState(null); // Phase 3: { balance, recent: [] } or null
  const [sharedDocs, setSharedDocs]   = useState([]);   // pkg57 — documents the firm shared here
  const [dlDoc, setDlDoc]             = useState('');   // pkg57 — shared doc id mid-download

  // Calendar sync (subscribe-to-iCal) state. `null` while loading; thereafter
  //   { active: false }                                — not enabled
  //   { active: true, feedUrl, webcalUrl, createdAt } — enabled
  const [calSync, setCalSync]           = useState(null);
  const [calModalOpen, setCalModalOpen] = useState(false);
  const [calBusy, setCalBusy]           = useState(false);
  const [calCopied, setCalCopied]       = useState(false);
  const [calError, setCalError]         = useState('');

  // Per-event .ics download: tracks which row is currently downloading so
  // the button can show a small loading state. Keyed by the merged-row
  // `key` field built in `allDates` below.
  const [importingKey, setImportingKey] = useState(null);

  // Phase 2: which discovery responses are visible to this client. Drives a
  // conditional menu card. If there's nothing visible, no card renders
  // (same rule as the sidebar link — empty state is hidden).
  const [discoveryResponses, setDiscoveryResponses] = useState([]);

  // pkg57 — best-effort: a hiccup hides the section rather than erroring.
  useEffect(() => {
    api.getMySharedDocuments().then(rows => setSharedDocs(rows || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.getLinks().then(setLinks).catch(() => {});
    api.getDates().then(setDates).catch(() => {});
    api.getGlobalDates().then(setGlobalDates).catch(() => {});
    api.getStatus().then(setStatus).catch(() => {});
    api.getMyTrust().then(setTrust).catch(() => {}); // silent fail = widget hidden
    api.listClientDiscovery().then(setDiscoveryResponses).catch(() => setDiscoveryResponses([]));
    api.getCalendarSync().then(setCalSync).catch(() => setCalSync({ active: false }));
  }, []);

  // ── Calendar sync handlers ────────────────────────────────────────────────
  async function handleEnableCalSync() {
    setCalBusy(true);
    setCalError('');
    try {
      const next = await api.enableCalendarSync();
      setCalSync(next);
    } catch (err) {
      setCalError(err.message || 'Could not enable sync.');
    } finally {
      setCalBusy(false);
    }
  }

  async function handleDisableCalSync() {
    if (!window.confirm('Stop syncing? Your calendar app will stop receiving updates within a few hours, and the subscription link will stop working. You can re-enable it any time.')) return;
    setCalBusy(true);
    setCalError('');
    try {
      const next = await api.disableCalendarSync();
      setCalSync(next);
    } catch (err) {
      setCalError(err.message || 'Could not disable sync.');
    } finally {
      setCalBusy(false);
    }
  }

  function handleCopyFeedUrl() {
    if (!calSync || !calSync.feedUrl) return;
    // navigator.clipboard is async; fall back to a tiny <textarea> hack for
    // environments where it isn't available (older mobile Safari).
    const copyText = calSync.feedUrl;
    const done = () => { setCalCopied(true); setTimeout(() => setCalCopied(false), 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(copyText).then(done).catch(() => {
        const ta = document.createElement('textarea');
        ta.value = copyText; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); done(); } catch {}
        document.body.removeChild(ta);
      });
    } else {
      const ta = document.createElement('textarea');
      ta.value = copyText; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); done(); } catch {}
      document.body.removeChild(ta);
    }
  }

  function parseDate(dateStr) {
    const s = dateStr.substring(0, 10);
    const [yr, mo, dy] = s.split('-').map(Number);
    return { yr, mo, dy };
  }

  function formatDate(dateStr) {
    const { yr, mo, dy } = parseDate(dateStr);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[mo-1]} ${dy}, ${yr}`;
  }

  function formatDateTime(isoStr) {
    if (!isoStr) return null;
    const d = new Date(isoStr);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  }

  function dateStatus(dateStr) {
    const { yr, mo, dy } = parseDate(dateStr);
    const today = new Date(); today.setHours(0,0,0,0);
    const dt = new Date(yr, mo - 1, dy);
    if (dt < today) return 'past';
    return (dt - today) / 86400000 <= 14 ? 'soon' : 'future';
  }

  // Format a TIME column value as 12-hour with AM/PM, e.g. "10:00 AM".
  // Returns null if t is empty/invalid. Accepts "HH:MM" or "HH:MM:SS".
  function fmtTime12(t) {
    if (!t) return null;
    const m = /^(\d{2}):(\d{2})/.exec(String(t));
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = m[2];
    const period = h >= 12 ? 'PM' : 'AM';
    h = h % 12; if (h === 0) h = 12;
    return `${h}:${min} ${period}`;
  }
  function fmtTimeRange(start, end) {
    const s = fmtTime12(start);
    const e = fmtTime12(end);
    if (!s) return '';
    if (!e || e === s) return s;
    return `${s} – ${e}`;
  }

  // Card status badge
  function StatusBadge({ submitted, saved }) {
    if (submitted) return (
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1e7e5e', flexShrink: 0, display: 'inline-block' }} />
        <span style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#1e7e5e', fontWeight: 600 }}>
          Submitted {formatDateTime(submitted)}
        </span>
      </div>
    );
    if (saved) return (
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#b7870a', flexShrink: 0, display: 'inline-block' }} />
        <span style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#b7870a', fontWeight: 600 }}>
          Saved {formatDateTime(saved)} — not yet submitted
        </span>
      </div>
    );
    return (
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ccc', flexShrink: 0, display: 'inline-block' }} />
        <span style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#aaa' }}>Not started</span>
      </div>
    );
  }

  // Merge and sort dates. Per-client dates carry an optional time range;
  // firm-wide ("global") dates remain all-day-only by design.
  const allDates = [
    ...dates.map(d => ({
      key: `i-${d.id}`, id: d.id, sortKey: `${d.date.substring(0,10)} ${d.start_time || ''}`,
      dateLabel: formatDate(d.date), dateLabel2: null,
      timeRange: fmtTimeRange(d.start_time, d.end_time),
      status: dateStatus(d.date), description: d.description,
      location: d.location, isGlobal: false,
    })),
    ...globalDates.map(d => {
      const start = d.start_date.substring(0,10);
      const end   = d.end_date ? d.end_date.substring(0,10) : null;
      return {
        key: `g-${d.id}`, id: d.id, sortKey: start,
        dateLabel: formatDate(start), dateLabel2: end ? formatDate(end) : null,
        timeRange: '',
        status: end ? dateStatus(end) : dateStatus(start),
        description: d.description, location: d.location, isGlobal: true,
      };
    }),
  ].sort((a, b) => a.sortKey.localeCompare(b.sortKey));

  // Pull a single event's .ics from the API, then trigger a browser download.
  // This works the same on every platform: the OS sees a text/calendar file
  // and offers to open it in whichever calendar app the user has set up.
  async function handleImportDate(item) {
    setImportingKey(item.key);
    try {
      const blob = await api.downloadEventIcs(item.isGlobal, item.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Filename the OS will show in the download prompt. Sanitize so the
      // value is safe in any download UI (Samsung's download manager rejects
      // some punctuation, Safari mangles certain Unicode).
      const safe = (item.description || 'event')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 64) || 'event';
      a.download = `${safe}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Slight delay before revoking so Safari doesn't cancel the in-flight
      // download. URL.createObjectURL keeps the blob alive in memory; we
      // free it once the click has had time to register.
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      window.alert(`Could not download event: ${err.message || err}`);
    } finally {
      setImportingKey(null);
    }
  }

  const hasVisibleDiscovery = modules.some(m => m.key === 'discovery_response') && discoveryResponses.length > 0;

  // Cards are built directly from the assigned modules list. discovery_response
  // gets its custom badge (response count) instead of submitted/saved dates.
  // Order follows module.sort_order (the API already returns them sorted).
  // Card numbers are recomputed locally so the visible cards always show 01,
  // 02, 03... regardless of gaps in the catalog.
  const cards = modules
    .filter(m => {
      // Discovery still requires a visible response (avoid empty state).
      if (m.key === 'discovery_response') return hasVisibleDiscovery;
      return true;
    })
    .map((m, i) => {
      const st = statusForModule(m.key, status);
      // Built-in description comes from the module row (seeded from the
      // legacy hardcoded values), so the card description matches what
      // the old static array used to render. Content modules get whatever
      // the admin set in the module catalog.
      let desc = m.description || '';
      // Special case for Discovery: append the set count when there's more
      // than one. The seeded description already ends with a period, so
      // trim that off before appending to avoid "...with you. (3 sets)."
      if (m.key === 'discovery_response' && discoveryResponses.length > 1) {
        desc = `${desc.replace(/\.\s*$/, '')} (${discoveryResponses.length} sets).`;
      }
      return {
        key:        m.id,
        num:        String(i + 1).padStart(2, '0'),
        to:         moduleHref(m),
        title:      m.label,
        desc,
        submitted:  st.submitted,
        saved:      st.saved,
        isDiscovery: m.key === 'discovery_response',
      };
    });

  // pkg21 — firm-private AI modules render as additional cards after the
  // platform modules, numbering continued. The /me list endpoint includes
  // submitted_at when the client has a prior submission, which drives the
  // same StatusBadge the other cards use (these forms have no separate
  // saved-draft state — submit upserts).
  const allCards = [
    ...cards,
    ...firmModules.map((m, i) => ({
      key:        `fm-${m.id}`,
      num:        String(cards.length + i + 1).padStart(2, '0'),
      to:         `/ai-modules/${m.id}`,
      title:      m.label,
      desc:       m.description || 'Complete this form for your attorney.',
      submitted:  m.submitted_at,
      saved:      null,
      isDiscovery: false,
    })),
  ];

  // pkg57 — one-tap Word download of a shared document.
  async function downloadSharedDoc(d) {
    setDlDoc(d.id);
    try {
      const blob = await api.downloadSharedDocument(d.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${String(d.label || 'Document').replace(/[^a-zA-Z0-9]/g, '_')}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { /* non-fatal; button re-enables */ }
    setDlDoc('');
  }

  return (
    <div>
      <div className="card" style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontFamily: 'Arial, sans-serif' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Payment methods &amp; autopay</div>
          <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>Save a card or bank account, and choose whether new invoices are charged automatically.</div>
        </div>
        <Link to="/payment-methods" className="btn btn-sm btn-primary">Payment methods &amp; autopay</Link>
      </div>
      {/* pkg57 — Documents from your firm */}
      {sharedDocs.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div className="section-head" style={{ marginTop: 0 }}>Documents from your firm</div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {sharedDocs.map((d, i) => (
              <div key={d.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                padding: '12px 16px', borderBottom: i < sharedDocs.length - 1 ? '1px solid #eee' : 'none',
                fontFamily: 'Arial, sans-serif', fontSize: 13,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{d.label}</div>
                  {d.shared_at && (
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                      Shared {new Date(d.shared_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <button
                  className="btn btn-sm"
                  disabled={dlDoc === d.id}
                  onClick={() => downloadSharedDoc(d)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {dlDoc === d.id ? 'Downloading…' : '↓ Download'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Important Dates */}
      {allDates.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div className="section-head" style={{ marginTop: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span>Important Dates</span>
            {/* Show sync button only if the client has at least one personal date
                (the feed excludes firm-wide dates by design) OR if sync is
                already active (so they can manage / disable it). */}
            {(dates.length > 0 || (calSync && calSync.active)) && (
              <button
                type="button"
                onClick={() => { setCalModalOpen(true); setCalError(''); setCalCopied(false); }}
                className="btn btn-sm"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: calSync && calSync.active ? '#e8f7ef' : '#fff',
                  borderColor: calSync && calSync.active ? '#1a7a5e' : '#ccc',
                  color: calSync && calSync.active ? '#1a7a5e' : '#333',
                }}
                title={calSync && calSync.active ? 'Sync is active — tap to manage' : 'Sync these dates to your phone or computer calendar'}
              >
                <span style={{ fontSize: 14 }}>📅</span>
                {calSync && calSync.active ? 'Sync active' : 'Sync to my calendar'}
              </button>
            )}
          </div>
          <div className="table-wrap">
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#0F1838', color: '#fff' }}>
                    <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, width: 150 }}>Date</th>
                    <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600 }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {allDates.map((d, i) => {
                    const isLink = d.location && (d.location.startsWith('http://') || d.location.startsWith('https://'));
                    const isImporting = importingKey === d.key;
                    return (
                      <tr key={d.key} style={{ borderBottom: '1px solid #eee', background: d.isGlobal ? '#f0f4ff' : (i%2===0?'#fff':'#fafafa') }}>
                        <td style={{ padding: '9px 14px', fontWeight: 600, verticalAlign: 'top', whiteSpace: 'nowrap',
                          color: d.status==='past'?'#aaa':d.status==='soon'?'#B85042':'#0F1838' }}>
                          {d.dateLabel}
                          {d.dateLabel2 && <div style={{ fontWeight:400, fontSize:11, color:'#888', marginTop:2 }}>through {d.dateLabel2}</div>}
                          {d.timeRange && <div style={{ fontWeight:400, fontSize:11, color: d.status==='past'?'#aaa':'#666', marginTop:2 }}>{d.timeRange}</div>}
                          {d.status==='soon' && <span style={{ marginLeft:6, fontSize:10, background:'#B85042', color:'#fff', borderRadius:3, padding:'1px 5px' }}>SOON</span>}
                          {/* Per-event download. Lives inline under the date so the
                              table stays at 2 columns and the button is visible on
                              narrow phone viewports without horizontal scrolling.
                              Downloads a single .ics file the OS hands to whichever
                              calendar app the user has set up (Apple, Samsung,
                              Google, Outlook, etc.) for one-tap import. */}
                          <div style={{ marginTop: 6 }}>
                            <button
                              type="button"
                              onClick={() => handleImportDate(d)}
                              disabled={isImporting}
                              title="Download this event as a calendar file you can add to your phone or computer"
                              style={{
                                background: 'none',
                                border: '1px solid #2f5496',
                                color: '#2f5496',
                                borderRadius: 3,
                                padding: '3px 8px',
                                fontSize: 11,
                                fontFamily: 'Arial, sans-serif',
                                fontWeight: 400,
                                cursor: isImporting ? 'wait' : 'pointer',
                                opacity: isImporting ? 0.6 : 1,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {isImporting ? '…' : '📥 Import'}
                            </button>
                          </div>
                        </td>
                        <td style={{ padding: '9px 14px', verticalAlign: 'top', color: d.status==='past'?'#aaa':'#333' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                            {d.isGlobal && <span style={{ fontSize:10, background:'#2f5496', color:'#fff', borderRadius:3, padding:'1px 6px', flexShrink:0 }}>FIRM</span>}
                            {d.description}
                          </div>
                          {d.location && (
                            <div style={{ marginTop:4, fontSize:12 }}>
                              {isLink
                                ? <a href={d.location} target="_blank" rel="noopener noreferrer" style={{ color:'#2f5496', textDecoration:'none' }}>🔗 {d.location}</a>
                                : <span style={{ color:'#555' }}>📍 {d.location}</span>}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <p className="page-subtitle" style={{ marginBottom: 4, marginTop: 0 }}>Welcome,</p>
      <h1 className="page-title">{client?.name}</h1>
      <p className="page-subtitle">
        This is your secure client portal{firm.firm_name ? ' with ' + firm.firm_name : ''}. Use the options below to submit
        information for your case.{firm.phone ? ' If you have questions, call us at ' + firm.phone + '.' : ''}
      </p>

      {/* Menu cards with status */}
      <div className="menu-grid">
        {allCards.map(card => (
          <Link key={card.key} to={card.to} className="menu-item">
            <div className="menu-item-number">{card.num}</div>
            <div className="menu-item-title">{card.title}</div>
            <div className="menu-item-desc">{card.desc}</div>
            {card.isDiscovery ? (
              // Custom status indicator for the Discovery card — there's no
              // single "submitted at" timestamp because there are multiple
              // responses with their own state; just count how many are open.
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#2f5496', flexShrink: 0, display: 'inline-block' }} />
                <span style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#2f5496', fontWeight: 600 }}>
                  {discoveryResponses.length} response{discoveryResponses.length === 1 ? '' : 's'} to review
                </span>
              </div>
            ) : (
              <StatusBadge submitted={card.submitted} saved={card.saved} />
            )}
          </Link>
        ))}
      </div>

      {/* Resources */}
      {links.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div className="section-head">Resources from Your Attorney</div>
          <div className="card">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {links.map(link => (
                <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer"
                  style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
                    border:'1px solid #ddd', borderRadius:4, textDecoration:'none',
                    color:'#0F1838', background:'#fafafa', fontFamily:'Arial, sans-serif',
                    fontSize:14, fontWeight:500 }}>
                  <span style={{ fontSize:18 }}>🔗</span>
                  {link.label}
                  <span style={{ marginLeft:'auto', fontSize:11, color:'#888' }}>→</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Trust Account Balance (Phase 3) — only shown if there's any trust history */}
      {trust && (parseFloat(trust.balance) !== 0 || (trust.recent && trust.recent.length > 0)) && (
        <div style={{ marginTop: 24 }}>
          <div className="section-head">Your Trust Account</div>
          <div className="card">
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 12, marginBottom: trust.recent && trust.recent.length > 0 ? 16 : 0,
            }}>
              <div>
                <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888',
                              textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                  Current Balance
                </div>
                <div style={{ fontSize: 28, fontFamily: 'Arial, sans-serif', fontWeight: 600,
                              color: parseFloat(trust.balance) > 0 ? '#1a7a5e' : '#0F1838' }}>
                  ${(parseFloat(trust.balance) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </div>
              </div>
              <div style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#666',
                            maxWidth: 380, textAlign: 'right', lineHeight: 1.4 }}>
                Funds your firm holds in trust on your behalf. These are your funds and remain yours
                until applied to invoices or returned to you.
              </div>
            </div>
            {trust.recent && trust.recent.length > 0 && (
              <div className="table-wrap">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: 'Arial, sans-serif' }}>
                  <thead>
                    <tr style={{ background: '#0F1838', color: '#fff' }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left', width: 110, fontWeight: 600 }}>Date</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600 }}>Description</th>
                      <th style={{ padding: '6px 10px', textAlign: 'right', width: 110, fontWeight: 600 }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trust.recent.map((t, i) => {
                      const amt = parseFloat(t.amount) || 0;
                      return (
                        <tr key={t.id} style={{ borderBottom: '1px solid #eee', background: i%2===0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '7px 10px' }}>{formatDate(t.transaction_date)}</td>
                          <td style={{ padding: '7px 10px', color: '#333' }}>
                            {t.memo || (t.type === 'deposit' ? 'Deposit'
                                      : t.type === 'fee_transfer' ? 'Earned-fee transfer to firm'
                                      : t.type === 'disbursement' ? 'Disbursement'
                                      : 'Adjustment')}
                          </td>
                          <td style={{
                            padding: '7px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600,
                            color: amt >= 0 ? '#1a7a5e' : '#B85042',
                          }}>
                            {amt >= 0 ? '+' : '−'}${Math.abs(amt).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-title" style={{ fontSize: 15 }}>{firm.firm_name || 'Your Firm'}</div>
        <div style={{ fontSize:13, color:'#888', fontFamily:'Arial, sans-serif', marginTop:8, lineHeight:1.8 }}>
          {firm.business_street && <>{firm.business_street}<br /></>}
          {(firm.business_city || firm.business_state || firm.business_zip) && (
            <>{[firm.business_city,
                [firm.business_state, firm.business_zip].filter(Boolean).join(' ')]
              .filter(Boolean).join(', ')}<br /></>
          )}
          {firm.phone && <>{firm.phone}<br /></>}
          {firm.client_contact_email}
        </div>
      </div>

      {/* ── Calendar sync modal ────────────────────────────────────────────
          Two states:
            • Sync not yet enabled → big "Enable sync" button
            • Sync active          → webcal:// one-tap link + copy URL + stop  */}
      {calModalOpen && (
        <div
          onClick={() => setCalModalOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8, maxWidth: 520, width: '100%',
              padding: 24, fontFamily: 'Arial, sans-serif',
              maxHeight: '90vh', overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontFamily: 'Georgia, serif', color: '#0F1838' }}>
                Sync your important dates
              </h2>
              <button
                type="button"
                onClick={() => setCalModalOpen(false)}
                aria-label="Close"
                style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: '#888', cursor: 'pointer', padding: 0, marginLeft: 12 }}
              >
                ×
              </button>
            </div>

            {calError && (
              <div style={{ background: '#fdf0ec', border: '1px solid #f5c6c3', color: '#B85042', padding: '8px 12px', borderRadius: 4, fontSize: 13, marginBottom: 12 }}>
                {calError}
              </div>
            )}

            {/* Loading state */}
            {!calSync && (
              <p style={{ fontSize: 13, color: '#888', margin: 0 }}>Loading…</p>
            )}

            {/* NOT-ACTIVE state: explain + offer enable */}
            {calSync && !calSync.active && (
              <>
                <p style={{ fontSize: 13, color: '#444', lineHeight: 1.55, marginTop: 0 }}>
                  Subscribe your phone or computer calendar to your case dates from this portal.
                  Any time we add, change, or remove a date, your calendar will update automatically
                  within a few hours. Only dates added specifically to your portal are included —
                  firm-wide dates are not.
                </p>
                <p style={{ fontSize: 12, color: '#888', lineHeight: 1.55 }}>
                  You can stop syncing at any time. Set your own reminders in your calendar app once
                  you're subscribed.
                </p>
                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="btn" onClick={() => setCalModalOpen(false)}>Cancel</button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleEnableCalSync}
                    disabled={calBusy}
                  >
                    {calBusy ? 'Enabling…' : 'Enable sync'}
                  </button>
                </div>
              </>
            )}

            {/* ACTIVE state: one-tap link + copy + stop */}
            {calSync && calSync.active && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1a7a5e', display: 'inline-block' }} />
                  <span style={{ fontSize: 13, color: '#1a7a5e', fontWeight: 600 }}>Sync is active</span>
                </div>
                <p style={{ fontSize: 13, color: '#444', lineHeight: 1.55, marginTop: 0 }}>
                  Your calendar app will refresh these dates automatically (typically every few hours).
                </p>

                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                    iPhone, iPad, or Mac
                  </div>
                  <a
                    href={calSync.webcalUrl}
                    className="btn btn-primary btn-block"
                    style={{ textDecoration: 'none', display: 'inline-block' }}
                  >
                    📅 Add to Apple Calendar
                  </a>
                  <p style={{ fontSize: 11, color: '#888', marginTop: 6, marginBottom: 0 }}>
                    Opens your default calendar app and offers to subscribe.
                  </p>
                </div>

                <div style={{ marginTop: 18 }}>
                  <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                    Android, Google Calendar, Outlook, or other (paste URL)
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      readOnly
                      value={calSync.feedUrl}
                      onFocus={e => e.target.select()}
                      style={{
                        flex: '1 1 240px', padding: '7px 10px', fontSize: 12,
                        border: '1px solid #ccc', borderRadius: 4, fontFamily: 'monospace',
                        background: '#fafafa',
                      }}
                    />
                    <button type="button" className="btn btn-sm" onClick={handleCopyFeedUrl}>
                      {calCopied ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                  <p style={{ fontSize: 11, color: '#888', marginTop: 6, marginBottom: 0 }}>
                    In Google Calendar (web): <em>Other calendars → + → From URL</em>.{' '}
                    In Outlook: <em>Add calendar → Subscribe from web</em>.
                  </p>
                </div>

                <div style={{ marginTop: 22, paddingTop: 14, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <button
                    type="button"
                    onClick={handleDisableCalSync}
                    disabled={calBusy}
                    style={{
                      background: 'none', border: '1px solid #B85042', color: '#B85042',
                      borderRadius: 3, padding: '6px 14px', cursor: calBusy ? 'wait' : 'pointer',
                      fontSize: 12, fontFamily: 'Arial, sans-serif',
                    }}
                  >
                    {calBusy ? 'Stopping…' : 'Stop syncing'}
                  </button>
                  <button type="button" className="btn" onClick={() => setCalModalOpen(false)}>Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
